import { Coffee, MapPin, Truck, Users } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';

export default function HammerFist() {
  const [showOrderModal, setShowOrderModal] = useState(false);
  const isSoldOut = false;
  const soldOutMessage = "We're busy roasting — back soon!";

  const scrollToDetails = () => {
    const detailsSection = document.getElementById('details-section');
    detailsSection?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleOrderClick = () => {
    if (isSoldOut) return;
    setShowOrderModal(true);
  };

  const handleCloseModal = () => {
    setShowOrderModal(false);
  };

  return (
    <div className="min-h-screen bg-[#132947]">
      <Header />
      {/* Hero Section */}
      <div className="relative min-h-screen overflow-hidden">
        {/* Background with parallax effect */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1455218873597-01ea7d84c70d?w=1920&q=80)',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-[#132947]/80 via-[#132947]/90 to-[#132947]" />
        </div>

        {/* Enhanced decorative elements */}
        <div className="absolute top-20 right-10 w-72 h-72 bg-rose-700/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-blue-900/10 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-radial from-rose-700/5 to-transparent rounded-full blur-2xl" />

        <div className="relative min-h-screen flex items-center justify-center px-6 max-w-7xl mx-auto py-20">
          <div className="grid lg:grid-cols-2 gap-16 items-center w-full">
            {/* Left side - Content */}
            <div className="text-center lg:text-left space-y-6">
              {/* Main heading with refined typography and decorative line */}
              <div className="relative">
                <div className="absolute -left-4 top-8 w-1 h-32 bg-gradient-to-b from-rose-600 to-transparent rounded-full hidden lg:block" />
                <h1 className="text-6xl md:text-8xl font-bold text-white mb-4 tracking-tight leading-none">
                  Hammer
                  <span className="block text-transparent bg-clip-text bg-gradient-to-r from-rose-600 to-amber-500 mt-2">Fist</span>
                </h1>
              </div>

              {/* Subtitle with better spacing and visual separator */}
              <div className="space-y-4 pt-2">
                <div className="flex items-center gap-3 justify-center lg:justify-start">
                  <div className="h-px w-12 bg-gradient-to-r from-transparent to-rose-600/50" />
                  <span className="text-rose-500/70 text-sm font-semibold uppercase tracking-wider">A Blend for Friends Who've Seen Everything</span>
                  <div className="h-px w-12 bg-gradient-to-l from-transparent to-rose-600/50" />
                </div>

                <p className="text-xl md:text-2xl text-white/90 leading-relaxed">
                  Guatemala. Ethiopia. Tanzania. Three origins, better together.
                </p>

                <p className="text-lg md:text-xl text-white/70 font-light">
                  A rich, layered blend built on a steady Guatemalan foundation, brightened by Ethiopian Natural, and deepened with Tanzanian character.
                </p>
              </div>

              {/* CTA buttons with improved styling */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start pt-4">
                <button
                  onClick={handleOrderClick}
                  disabled={isSoldOut}
                  className={`group relative px-12 py-6 rounded-xl font-bold text-xl transition-all duration-300 inline-flex items-center justify-center gap-3 ${
                    isSoldOut
                      ? 'bg-white/10 text-white/50 cursor-not-allowed'
                      : 'bg-gradient-to-r from-rose-600 to-amber-500 text-white hover:from-rose-700 hover:to-amber-600 shadow-2xl shadow-rose-700/40 transform hover:scale-105 hover:shadow-rose-700/60'
                  } overflow-hidden`}
                >
                  {!isSoldOut && <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />}
                  <Coffee className={`w-6 h-6 ${!isSoldOut && 'group-hover:rotate-12'} transition-transform`} />
                  {isSoldOut ? soldOutMessage : 'Order Now'}
                </button>
                <button
                  onClick={scrollToDetails}
                  className="bg-white/5 text-white border-2 border-white/20 px-10 py-6 rounded-xl font-semibold text-lg hover:bg-white/10 hover:border-rose-600/50 transition-all duration-300 backdrop-blur-md"
                >
                  Learn More
                </button>
              </div>
            </div>

            {/* Right side - Product image with enhanced styling */}
            <div className="flex items-center justify-center lg:justify-end relative">
              <div className="absolute inset-0 bg-gradient-to-br from-rose-600/20 to-transparent rounded-3xl blur-3xl scale-110" />
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-br from-rose-600/30 to-transparent rounded-3xl blur-xl" />
                <img
                  src="/ChatGPT_Image_Aug_18,_2026,_09_43_45_AM.png"
                  alt="Hammer Fist Coffee"
                  className="relative w-full max-w-md rounded-2xl shadow-2xl transform hover:scale-105 transition-transform duration-500 border-2 border-white/10"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Details Section */}
      <div id="details-section" className="max-w-4xl mx-auto px-6 py-20">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            About Hammer Fist
          </h2>
          <div className="w-20 h-1 bg-rose-600 mx-auto rounded-full" />
        </div>

        <div className="bg-gradient-to-br from-rose-600/10 to-transparent border border-rose-600/20 rounded-2xl p-8 md:p-12">
          <div className="flex items-center justify-center gap-4 mb-8">
            <Users className="w-8 h-8 text-rose-600" />
          </div>

          <div className="text-white/80 text-lg leading-relaxed max-w-3xl mx-auto space-y-4">
            <p className="font-semibold text-white text-2xl text-center mb-6">
              In 1997, Rach took a self-defense class.
              <br />
              Thankfully, someone brought a video camera.
            </p>

            <p>
              Buried somewhere on an old VHS tape is footage from graduation day, including an instructor enthusiastically demonstrating one particularly memorable move:
            </p>

            <p className="font-bold text-rose-400 text-xl text-center">
              "HAMMER FIST!"
            </p>

            <p>
              For reasons that are probably impossible to explain to anyone who wasn't there, it became one of those things that never stopped being funny.
            </p>

            <p>
              And that's kind of how it goes with people who have been part of your life for a long time. You collect these completely random little moments until, somehow, they become part of the story.
            </p>

            <p className="font-medium text-white/90">
              Rach has been there for a lot of mine.
            </p>

            <p>
              She was there supporting <span className="font-semibold text-white">Fat Tuesday</span>, our high school band. She bought us a Talk Box at <span className="font-semibold text-white">Daddy's Junky Music</span>, which at the time felt like about the coolest piece of equipment anyone could possibly own. Years later, Rach and I got tattoos together on the same day in Georgetown — another little piece of our history permanently preserved.
            </p>

            <p>
              And now, all these years later, when we decided to start Bond Coffee, Rach showed up again and supported our Kickstarter.
            </p>

            <p>
              As my kids have gotten older, I've found myself thinking about all of this more.
            </p>

            <p>
              Maybe it's having a little more time to look backward. Maybe it's watching them make their own friends and memories. But lately, the emotions around these old stories seem to hit me harder than they used to.
            </p>

            <p className="font-medium text-white/90">
              You start to realize just how much life has happened.
              <br />
              And how lucky you are that some of the people who were there at the beginning are somehow still here.
            </p>

            <p>
              I love that I can still call people I've known for most of my life. That we can still hang out, laugh about things that happened decades ago, and then pick right back up with whatever is happening now.
            </p>

            <p>
              There's something incredibly comforting about being around people who knew you before the jobs, before the kids, before any of us had much idea where life was going.
            </p>

            <p className="font-medium text-white/90">
              They've seen different versions of you.
              <br />
              And somehow, they're still in your corner.
            </p>

            <p>
              Rach has been in mine for a very long time.
            </p>

            <p>
              So when she supported Bond, it felt like more than somebody backing our Kickstarter. It felt like another moment in a story that has been going on for decades.
            </p>

            <p>
              We're honored by it. We're proud to make this for her. And I'm incredibly grateful that after all these years, we're still adding new stories to the old ones.
            </p>

            <p className="font-medium text-white/90">
              And the coffee itself feels fitting.
            </p>

            <p>
              <span className="font-semibold text-white">Hammer Fist</span> is a blend of coffees from <span className="font-semibold text-white">Guatemala, Ethiopia and Tanzania</span>. The Guatemalan coffee gives it a rich, steady foundation. The Ethiopian Natural brings the brighter, fruitier side, and the Tanzanian adds another layer of character and complexity.
            </p>

            <p>
              Three very different origins, each bringing something of its own, but better when they come together.
            </p>

            <p className="text-white/70 italic">
              There's probably a friendship metaphor in there somewhere.
            </p>

            <p>
              Here's to everything that has already happened.
            </p>

            <p>
              And to everything we haven't gotten to yet.
            </p>

            <p>
              Because life has a funny way of coming at you fast.
            </p>

            <p>
              And if you're lucky, you have a few people beside you who have been there through just about all of it.
            </p>

            <p>
              People who know the stories.
            </p>

            <p>
              People who remember the old versions of you.
            </p>

            <p>
              People who are still there when the next chapter starts.
            </p>

            <p className="font-medium text-white/90">
              And every once in a while, life calls for one move you never forget.
            </p>

            <p className="font-bold text-rose-400 text-2xl text-center mt-8">
              HAMMER FIST.
            </p>

            <p className="text-white/70 italic text-center">
              A Blend for Friends Who've Seen Everything
            </p>
          </div>
        </div>
      </div>

      {/* Bean Composition Section */}
      <div className="max-w-4xl mx-auto px-6 pb-20">
        <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12">
          Coffee Composition
        </h2>

        <div className="bg-white/5 border border-white/10 rounded-2xl p-8 md:p-12 space-y-6">
          <h3 className="text-2xl font-bold text-white mb-4">Tasting Profile</h3>
          <p className="text-white/80 leading-relaxed">
            Hammer Fist is a blend of three origins — Guatemala, Ethiopia, and Tanzania. The Guatemalan gives it a rich, steady foundation, the Ethiopian Natural brings a brighter, fruitier side, and the Tanzanian adds another layer of character and complexity. Three very different origins, better when they come together.
          </p>
          <div className="grid md:grid-cols-2 gap-4 pt-4">
            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
              <h4 className="font-semibold text-white mb-2">Roast Level</h4>
              <p className="text-white/70">Medium Blend</p>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
              <h4 className="font-semibold text-white mb-2">Best Brewing Methods</h4>
              <p className="text-white/70">Drip, Pour Over, French Press</p>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
              <h4 className="font-semibold text-white mb-2">Origins</h4>
              <p className="text-white/70">Guatemala, Ethiopia, Tanzania</p>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
              <h4 className="font-semibold text-white mb-2">Size</h4>
              <p className="text-white/70">12 oz (340g)</p>
            </div>
          </div>
        </div>
      </div>

      {/* Pricing & Availability */}
      <div className="max-w-4xl mx-auto px-6 py-20">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Order Today
          </h2>
          <p className="text-white/70 text-xl">Fresh roasted and ready to ship</p>
        </div>

        <div className="bg-gradient-to-br from-rose-600/10 to-transparent border-2 border-rose-600/40 rounded-2xl p-8 md:p-12">
          <div className="text-center mb-12">
            <div className="inline-flex items-baseline gap-2 mb-2">
              <span className="text-5xl md:text-6xl font-bold text-white">$19</span>
              <span className="text-white/70 text-lg">per 12oz bag</span>
            </div>
            <p className="text-rose-500 font-semibold text-lg">
              Marblehead pricing
            </p>
          </div>

          <div className="space-y-3 mb-8">
            <div className="flex items-center justify-between bg-white/5 border border-white/10 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <MapPin className="w-5 h-5 text-rose-500" />
                <span className="text-white font-medium">Marblehead Delivery</span>
              </div>
              <span className="text-rose-500 font-semibold text-lg">$19</span>
            </div>

            <div className="flex items-center justify-between bg-white/5 border border-white/10 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <Truck className="w-5 h-5 text-rose-500" />
                <span className="text-white font-medium">US Shipping (included)</span>
              </div>
              <span className="text-white/90 font-semibold text-lg">$27</span>
            </div>
          </div>

          <div className="text-center">
            <button
              onClick={handleOrderClick}
              disabled={isSoldOut}
              className={`px-10 py-5 rounded-lg font-bold text-xl transition-all duration-300 inline-flex items-center gap-3 ${
                isSoldOut
                  ? 'bg-white/10 text-white/50 cursor-not-allowed'
                  : 'bg-gradient-to-r from-rose-600 to-amber-500 text-white hover:from-rose-700 hover:to-amber-600 shadow-xl shadow-rose-700/30 hover:scale-105 transform'
              }`}
            >
              <Coffee className="w-6 h-6" />
              {isSoldOut ? soldOutMessage : 'Order Hammer Fist'}
            </button>
            <p className="text-white/60 text-sm mt-4">
              {isSoldOut ? 'Check back soon!' : 'Free local delivery • Shipping included for non-Marblehead orders'}
            </p>
          </div>
        </div>
      </div>

      {/* Final CTA */}
      <div className="border-t border-white/10 py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Experience Hammer Fist?
          </h3>
          <p className="text-white/70 text-xl mb-8">
            Order your bag today and taste a blend built on friendship
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <button
              onClick={handleOrderClick}
              disabled={isSoldOut}
              className={`px-10 py-5 rounded-lg font-bold text-xl transition-all duration-300 inline-flex items-center justify-center gap-3 ${
                isSoldOut
                  ? 'bg-white/10 text-white/50 cursor-not-allowed'
                  : 'bg-gradient-to-r from-rose-600 to-amber-500 text-white hover:from-rose-700 hover:to-amber-600 shadow-xl shadow-rose-700/30'
              }`}
            >
              <Coffee className="w-6 h-6" />
              {isSoldOut ? soldOutMessage : 'Order Now'}
            </button>
            <Link
              to="/"
              className="bg-white/10 text-white border border-white/20 px-8 py-5 rounded-lg font-semibold text-lg hover:bg-white/20 transition-all duration-300 inline-flex items-center justify-center"
            >
              Explore Bond Coffee
            </Link>
          </div>
          <p className="text-white/50 text-sm">
            Questions? Contact us at hello@bondcoffee.com
          </p>
        </div>
      </div>

      <Footer />

      {/* Order Modal */}
      {showOrderModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-[#132947] border-2 border-rose-600/30 rounded-2xl max-w-2xl w-full p-8 relative shadow-2xl my-8 max-h-[90vh] overflow-y-auto">
            <button
              onClick={handleCloseModal}
              className="absolute top-4 right-4 text-white/60 hover:text-white transition-colors z-10 bg-[#132947]/80 rounded-full p-1"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <h2 className="text-3xl font-bold text-white mb-4 text-center">Choose Your Option</h2>
            <p className="text-white/70 text-center mb-8">Select delivery or shipping</p>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Marblehead Delivery */}
              <div className="bg-white/5 border border-white/10 rounded-xl p-6 hover:border-rose-600/50 transition-all group">
                <div className="text-center mb-4">
                  <MapPin className="w-12 h-12 text-rose-500 mx-auto mb-3" />
                  <h3 className="text-xl font-bold text-white mb-2">Marblehead Delivery</h3>
                  <p className="text-white/70 text-sm mb-4">Free local delivery</p>
                </div>
                <div className="text-center mb-6">
                  <div className="text-4xl font-bold text-rose-500 mb-1">$19</div>
                  <div className="text-white/50 text-sm mb-3">per 12oz bag</div>
                </div>
                <a
                  href="https://buy.stripe.com/8x200kdhZgxxesl83M3VC0q"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full bg-gradient-to-r from-rose-600 to-amber-500 text-white py-3 rounded-lg font-bold text-center hover:from-rose-700 hover:to-amber-600 transition-all"
                >
                  Order - Marblehead
                </a>
              </div>

              {/* Shipping */}
              <div className="bg-white/5 border border-white/10 rounded-xl p-6 hover:border-rose-600/50 transition-all group">
                <div className="text-center mb-4">
                  <Truck className="w-12 h-12 text-rose-500 mx-auto mb-3" />
                  <h3 className="text-xl font-bold text-white mb-2">Ship Anywhere in the US</h3>
                  <p className="text-white/70 text-sm mb-4">Shipping included</p>
                </div>
                <div className="text-center mb-6">
                  <div className="text-4xl font-bold text-rose-500 mb-1">$27</div>
                  <div className="text-white/50 text-sm mb-3">per 12oz bag + shipping</div>
                </div>
                <a
                  href="https://buy.stripe.com/bJe5kEem3dll2JD1Fo3VC0p"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full bg-gradient-to-r from-rose-600 to-amber-500 text-white py-3 rounded-lg font-bold text-center hover:from-rose-700 hover:to-amber-600 transition-all"
                >
                  Order - Shipped
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
